/**
 * End-of-Day (EOD) Summary Report page.
 *
 * Access: clinic_owner · founder · accounts · super_admin · front_desk —
 * enforced on the API (`require_roles(*EOD_ROLES)`) and mirrored in the
 * sidebar gate. Audiologist / referral partners never see the nav item.
 * `front_desk` was added 2026-02 (Reports Workstream · Task-A) because
 * EOD is a Front-Desk operational document. Branch scoping on the API
 * still restricts a front_desk user to branches they are attached to.
 *
 * Delivery options at the top of the report:
 *   1. Print / Export PDF — window.print() scoped by
 *      `body.printing-eod-report` + `@media print` in App.css.
 *   2. Send via Email      — client renders A4 canvas to PDF via
 *      html2canvas + jsPDF, POSTs base64 to
 *      `POST /api/reports/eod/email` which attaches it via Resend.
 *   3. Send via WhatsApp   — pre-fills a wa.me deep-link with a
 *      text summary and a note to "check email for the PDF".
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import useBranchClinic from '../../hooks/useBranchClinic';
import axios from 'axios';
import { Printer, Mail, MessageCircle, Loader2, CalendarDays, Building2 } from 'lucide-react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { useAuth } from '../../AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// A4 pixel constants — kept identical to the Report 2 canvas so the
// exported PDF from either flow looks the same at print time.
const A4_WIDTH_PX  = 794;
const A4_HEIGHT_PX = 1123;

const EOD_ROLES = ['clinic_owner', 'super_admin', 'founder', 'accounts', 'front_desk'];

const fmtINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

const StatCard = ({ label, value, testid }) => (
  <div className="flex-1 min-w-[110px] border border-slate-200 rounded px-3 py-2 bg-white" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
    <div className="text-lg font-bold text-slate-900">{value}</div>
  </div>
);

const SectionCard = ({ title, children, testid }) => (
  <section
    className="border border-slate-200 rounded bg-white overflow-hidden"
    data-testid={testid}
  >
    <div className="px-3 py-1.5 border-b border-slate-200 bg-slate-50 text-[11px] font-bold text-slate-700 uppercase tracking-wider">
      {title}
    </div>
    <div className="p-3">{children}</div>
  </section>
);

export default function EodReportPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Calendar-tab drill-in (2026-02): the historical calendar navigates
  // here with `?date=YYYY-MM-DD&branch=<id>` so the same EOD page
  // renders any past day. If the params are absent/malformed we fall
  // back to today + no branch pre-selection (existing default).
  const [date, setDate] = useState(() => {
    const urlDate = searchParams.get('date');
    if (urlDate && /^\d{4}-\d{2}-\d{2}$/.test(urlDate)) return urlDate;
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState(() => searchParams.get('branch') || '');
  const clinic = useBranchClinic(branchId);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [msg, setMsg] = useState('');
  // Cash Drawer · Increment 1 (2026-02) — Opening Cash is now
  // server-persisted per (clinic, branch, date). The previous
  // `localStorage['eod_cash_carry_${date}']` fallback leaked across
  // branches / devices and has been removed. Numeric semantics on
  // the page are unchanged:
  //   Cash in Drawer = Opening Cash + collections.by_method.cash
  const [cashCarryOver, setCashCarryOver] = useState(0);
  const [cashInput, setCashInput] = useState('0');
  const [cashMeta, setCashMeta] = useState({ opening_by_user_id: null, opening_at: null, closed: false });
  const [cashSaving, setCashSaving] = useState(false);
  const [cashMsg, setCashMsg] = useState('');
  const [cashDirty, setCashDirty] = useState(false);
  // Petty Cash (2026-02) — server-persisted `cash_outflows` rows with
  // kind="petty_cash". Displayed as a small operational panel; the
  // total is subtracted from the Closing Cash Handover line. The raw
  // `collections.by_method.cash` value is untouched and continues to
  // mean "sum of payments where method=cash".
  const [pettyEntries, setPettyEntries] = useState([]);
  const [pettyTotal, setPettyTotal] = useState(0);
  const [pettyAmountInput, setPettyAmountInput] = useState('');
  const [pettyReasonInput, setPettyReasonInput] = useState('');
  const [pettySaving, setPettySaving] = useState(false);
  const [pettyMsg, setPettyMsg] = useState('');
  // Actual Cash Count · Cash Variance (2026-02 · Phase 1).
  // Physical observation only — never modifies Opening Cash,
  // `by_method.cash`, Petty Cash or Expected Closing Cash. The
  // authoritative Variance / Status comes from the EOD `cash_handover`
  // payload the backend computes; this client only renders it.
  const [actualCashValue, setActualCashValue] = useState(null);
  const [actualCashInput, setActualCashInput] = useState('');
  const [actualCashMeta, setActualCashMeta] = useState({ actual_cash_at: null, actual_cash_by_user_id: null });
  const [actualCashSaving, setActualCashSaving] = useState(false);
  const [actualCashMsg, setActualCashMsg] = useState('');
  const [actualCashDirty, setActualCashDirty] = useState(false);
  // Branch Cash Management · Phase 2A (2026-02) — Bank Deposit is a
  // MOVEMENT of physical cash out of the drawer to the bank. Recorded
  // as a `cash_outflows` row with kind="bank_deposit" via the existing
  // Increment 2 endpoint. Never modifies payments / collections /
  // petty / opening / expected / actual / variance.
  const [bankEntries, setBankEntries] = useState([]);
  const [bankTotal, setBankTotal] = useState(0);
  const [bankAmountInput, setBankAmountInput] = useState('');
  const [bankReferenceInput, setBankReferenceInput] = useState('');
  const [bankSaving, setBankSaving] = useState(false);
  const [bankMsg, setBankMsg] = useState('');

  const isAllowed = EOD_ROLES.includes(user?.role);
  // Bank Deposit write gate (mirrors backend `_BANK_DEPOSIT_ROLES` —
  // Phase 2A extended to include front_desk). Read is universal within
  // EOD_ROLES. Server-side is authoritative; this is a UX hint only.
  const BANK_DEPOSIT_ROLES = ['clinic_owner', 'super_admin', 'founder', 'accounts', 'front_desk'];
  const canRecordBankDeposit = BANK_DEPOSIT_ROLES.includes(user?.role);

  // Effective branch for the per-branch cash-position endpoint. Single-
  // branch tenants get their sole branch by default; multi-branch users
  // must pick one before the drawer field becomes writable.
  const effectiveBranchId = useMemo(() => {
    if (branchId) return branchId;
    if (branches.length === 1) return branches[0]?.branch_id || '';
    return '';
  }, [branchId, branches]);
  const cashInputDisabled = !effectiveBranchId || cashMeta.closed;

  useEffect(() => {
    if (!isAllowed) {
      navigate('/patients', { replace: true });
    }
  }, [isAllowed, navigate]);

  // Load the caller's visible branches once — a chain-tier owner sees
  // every branch, a scoped role sees only their own. The dropdown is
  // rendered only when there's more than one option, so single-branch
  // tenants get a clean UI.
  useEffect(() => {
    if (!isAllowed) return;
    axios.get(`${API}/branches`)
      .then((r) => setBranches(Array.isArray(r.data) ? r.data : []))
      .catch(() => setBranches([]));
  }, [isAllowed]);

  // Cash Drawer · Increment 1 — fetch persisted Opening Cash on mount /
  // date change / branch change. No localStorage read-path.
  // Also pulls the outflows list (Increment 2) and derives petty cash
  // entries + total for the Petty Cash panel (2026-02).
  useEffect(() => {
    if (!isAllowed) return;
    setCashMsg('');
    setCashDirty(false);
    if (!effectiveBranchId) {
      setCashCarryOver(0);
      setCashInput('0');
      setCashMeta({ opening_by_user_id: null, opening_at: null, closed: false });
      setPettyEntries([]);
      setPettyTotal(0);
      return;
    }
    axios.get(`${API}/reports/eod/cash-position`, { params: { date, branch_id: effectiveBranchId } })
      .then((r) => {
        const val = Number(r.data?.opening_cash || 0);
        setCashCarryOver(val);
        setCashInput(String(val));
        setCashMeta({
          opening_by_user_id: r.data?.opening_by_user_id || null,
          opening_at: r.data?.opening_at || null,
          closed: !!r.data?.closed,
        });
        const all = Array.isArray(r.data?.outflows) ? r.data.outflows : [];
        setPettyEntries(all.filter((o) => o.kind === 'petty_cash'));
        setPettyTotal(Number(r.data?.petty_cash_total || 0));
        // Bank Deposit (Phase 2A) — same outflow feed, filter to the
        // bank-deposit kind and read the authoritative scalar.
        setBankEntries(all.filter((o) => o.kind === 'bank_deposit'));
        setBankTotal(Number(r.data?.bank_deposit_total || 0));
        // Actual Cash Count · Phase 1 hydration. `null` means "not
        // entered" — the UI shows the empty state instead of ₹0.
        const ac = r.data?.actual_cash;
        setActualCashValue(ac == null ? null : Number(ac));
        setActualCashInput(ac == null ? '' : String(Number(ac)));
        setActualCashMeta({
          actual_cash_at: r.data?.actual_cash_at || null,
          actual_cash_by_user_id: r.data?.actual_cash_by_user_id || null,
        });
        setActualCashDirty(false);
        setActualCashMsg('');
      })
      .catch(() => {
        setCashCarryOver(0);
        setCashInput('0');
        setCashMeta({ opening_by_user_id: null, opening_at: null, closed: false });
        setPettyEntries([]);
        setPettyTotal(0);
        setActualCashValue(null);
        setActualCashInput('');
        setActualCashMeta({ actual_cash_at: null, actual_cash_by_user_id: null });
        setActualCashDirty(false);
        setBankEntries([]);
        setBankTotal(0);
      });
  }, [date, effectiveBranchId, isAllowed]);

  const load = useCallback(async () => {
    if (!isAllowed) return;
    setLoading(true);
    setMsg('');
    try {
      const params = { date };
      if (branchId) params.branch_id = branchId;
      const r = await axios.get(`${API}/reports/eod`, { params });
      setData(r.data);
    } catch (e) {
      setMsg(e?.response?.data?.detail || 'Failed to load EOD report');
    } finally {
      setLoading(false);
    }
  }, [date, branchId, isAllowed]);

  useEffect(() => { load(); }, [load]);

  const totalCashInDrawer = useMemo(() => {
    const cashRx = Number(data?.collections?.by_method?.cash || 0);
    // Closing / Carry Forward Cash = Opening + Today's Cash Collection − Petty Cash
    // `collections.by_method.cash` is unchanged — it still means "sum
    // of payments with method=cash". Petty Cash is a separate physical
    // cash movement subtracted only here at the drawer handover line.
    return Number(cashCarryOver || 0) + cashRx - Number(pettyTotal || 0);
  }, [cashCarryOver, data, pettyTotal]);

  const onCashInputChange = (v) => {
    setCashInput(v);
    setCashDirty(true);
    setCashMsg('');
  };

  const onCashCarrySave = async () => {
    if (!effectiveBranchId) {
      setCashMsg('Select a branch to save opening cash');
      return;
    }
    const n = parseFloat(cashInput);
    if (Number.isNaN(n) || n < 0) {
      setCashMsg('Opening cash must be a non-negative number');
      return;
    }
    setCashSaving(true);
    setCashMsg('');
    try {
      const r = await axios.put(`${API}/reports/eod/cash-position/opening`, {
        date,
        branch_id: effectiveBranchId,
        opening_cash: n,
      });
      setCashCarryOver(Number(r.data?.opening_cash || 0));
      setCashInput(String(Number(r.data?.opening_cash || 0)));
      setCashMeta({
        opening_by_user_id: r.data?.opening_by_user_id || null,
        opening_at: r.data?.opening_at || null,
        closed: !!r.data?.closed,
      });
      setCashDirty(false);
      setCashMsg('Saved');
    } catch (e) {
      setCashMsg(e?.response?.data?.detail || 'Save failed');
    } finally {
      setCashSaving(false);
    }
  };

  // ---- Actual Cash Count · save (Phase 1) ----
  // Physical observation only. Uses the SAME cash-position endpoint
  // family; the server never modifies Opening Cash, Petty Cash or the
  // Expected Closing formula. Variance & status refresh from the next
  // EOD `cash_handover` payload — we re-run `load()` after save.
  const onActualCashSave = async () => {
    if (!effectiveBranchId) {
      setActualCashMsg('Select a branch to save actual cash');
      return;
    }
    const n = parseFloat(actualCashInput);
    if (Number.isNaN(n) || n < 0) {
      setActualCashMsg('Actual cash must be a non-negative number');
      return;
    }
    setActualCashSaving(true);
    setActualCashMsg('');
    try {
      const r = await axios.put(`${API}/reports/eod/cash-position/actual-cash`, {
        date,
        branch_id: effectiveBranchId,
        actual_cash: n,
      });
      setActualCashValue(Number(r.data?.actual_cash ?? 0));
      setActualCashInput(String(Number(r.data?.actual_cash ?? 0)));
      setActualCashMeta({
        actual_cash_at: r.data?.actual_cash_at || null,
        actual_cash_by_user_id: r.data?.actual_cash_by_user_id || null,
      });
      setActualCashDirty(false);
      setActualCashMsg('Saved');
      // Refresh the EOD payload so the derived variance / status in
      // cash_handover is authoritative (backend-computed).
      load();
    } catch (e) {
      setActualCashMsg(e?.response?.data?.detail || 'Save failed');
    } finally {
      setActualCashSaving(false);
    }
  };

  const onActualCashInputChange = (v) => {
    setActualCashInput(v);
    setActualCashDirty(true);
    setActualCashMsg('');
  };

  // ---- Bank Deposit · record deposit (Phase 2A · 2026-02) ----
  // Reuses the SAME outflow endpoint with kind="bank_deposit". The
  // server-side role gate (`_BANK_DEPOSIT_ROLES`, extended in Phase 2A
  // to include front_desk) + `_assert_cash_position_branch` + closed-
  // day protection remain authoritative. Never modifies payments /
  // collections / petty / opening / expected / actual / variance.
  const onBankDepositSave = async () => {
    if (!effectiveBranchId) {
      setBankMsg('Select a branch to record a deposit');
      return;
    }
    const amt = parseFloat(bankAmountInput);
    if (Number.isNaN(amt) || amt <= 0) {
      setBankMsg('Amount must be greater than zero');
      return;
    }
    const ref = (bankReferenceInput || '').trim();
    if (!ref) {
      setBankMsg('Reference / note is required');
      return;
    }
    setBankSaving(true);
    setBankMsg('');
    try {
      const r = await axios.post(`${API}/reports/eod/cash-position/outflow`, {
        date,
        branch_id: effectiveBranchId,
        kind: 'bank_deposit',
        amount: amt,
        // The outflow schema stores the operator's note in `reason`
        // and the deposit slip number in `reference`. We ask for one
        // combined "Reference / note" from the UI and store it in both
        // fields so the audit trail is legible either way.
        reason: ref,
        reference: ref,
      });
      setBankEntries((prev) => [...prev, r.data]);
      setBankTotal((prev) => Math.round((Number(prev || 0) + amt) * 100) / 100);
      setBankAmountInput('');
      setBankReferenceInput('');
      setBankMsg('Recorded');
      // Refresh EOD so `cash_remaining` on the drawer block reflects
      // the new deposit (backend-authoritative value).
      load();
    } catch (e) {
      setBankMsg(e?.response?.data?.detail || 'Failed to record deposit');
    } finally {
      setBankSaving(false);
    }
  };

  // ---- Petty Cash · add entry ----
  // Uses the EXISTING outflow endpoint from Increment 2
  // (POST /reports/eod/cash-position/outflow) with kind="petty_cash".
  // No new endpoint. Server enforces role + branch scoping.
  const onPettySave = async () => {
    if (!effectiveBranchId) {
      setPettyMsg('Select a branch to record petty cash');
      return;
    }
    const amt = parseFloat(pettyAmountInput);
    if (Number.isNaN(amt) || amt <= 0) {
      setPettyMsg('Amount must be greater than zero');
      return;
    }
    const reason = (pettyReasonInput || '').trim();
    if (!reason) {
      setPettyMsg('Reason is required');
      return;
    }
    setPettySaving(true);
    setPettyMsg('');
    try {
      const r = await axios.post(`${API}/reports/eod/cash-position/outflow`, {
        date,
        branch_id: effectiveBranchId,
        kind: 'petty_cash',
        amount: amt,
        reason,
      });
      setPettyEntries((prev) => [...prev, r.data]);
      setPettyTotal((prev) => Math.round((Number(prev || 0) + amt) * 100) / 100);
      setPettyAmountInput('');
      setPettyReasonInput('');
      setPettyMsg('Added');
    } catch (e) {
      setPettyMsg(e?.response?.data?.detail || 'Failed to add petty cash entry');
    } finally {
      setPettySaving(false);
    }
  };

  // -------- Delivery: Print --------
  const handlePrint = () => {
    document.body.classList.add('printing-eod-report');
    requestAnimationFrame(() => {
      window.print();
      const cleanup = () => document.body.classList.remove('printing-eod-report');
      window.addEventListener('afterprint', cleanup, { once: true });
      setTimeout(cleanup, 4000);
    });
  };

  // -------- Delivery: Email --------
  // Render the A4 canvas → PDF (jsPDF) → base64 → POST to /reports/eod/email.
  const handleEmail = async () => {
    const el = document.getElementById('eod-report-canvas');
    if (!el) { setMsg('EOD canvas not ready'); return; }
    setEmailing(true);
    setMsg('');
    try {
      const canvas = await html2canvas(el, { scale: 2, useCORS: true, backgroundColor: '#ffffff', logging: false });
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const A4_W_MM = 210;
      const A4_H_MM = 297;
      const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
      // Fit the whole canvas onto ONE A4 page — hard scale, letterboxed
      // vertically if the source image is taller than A4 (rare — the
      // canvas is already fixed at 794×1123 so it's exactly A4).
      const heightMM = Math.min(A4_H_MM, (canvas.height * A4_W_MM) / canvas.width);
      pdf.addImage(dataUrl, 'JPEG', 0, 0, A4_W_MM, heightMM, undefined, 'FAST');
      const b64 = pdf.output('datauristring').split(',')[1];
      const r = await axios.post(`${API}/reports/eod/email`, {
        date,
        pdf_base64: b64,
        filename: `EOD_${date}.pdf`,
      });
      setMsg(`✅ Emailed to ${r.data.to} · ${r.data.status || 'sent'}`);
    } catch (e) {
      setMsg(e?.response?.data?.detail || e?.message || 'Email failed');
    } finally {
      setEmailing(false);
    }
  };

  // -------- Delivery: WhatsApp --------
  const handleWhatsApp = () => {
    if (!data) return;
    const p = data.patient_summary || {};
    const t = data.tests_performed || {};
    const c = data.collections || {};
    const testLines = Object.entries(t.by_type || {})
      .filter(([, v]) => v > 0)
      .map(([k, v]) => `• ${k}: ${v}`)
      .join('%0A') || '• No tests logged';
    const methodLines = Object.entries(c.by_method || {})
      .filter(([, v]) => v > 0)
      .map(([k, v]) => `• ${k.toUpperCase()}: ${fmtINR(v)}`)
      .join('%0A') || '• No collections';
    const lines = [
      `*AUDINEXA · EOD Summary · ${data.date}*`,
      `${clinic?.name || 'Clinic'}`,
      '',
      `*Patients*`,
      `• New: ${p.new_patients || 0}  · Returning: ${p.returning_patients || 0}  · Footfall: ${p.total_footfall || 0}`,
      '',
      `*Tests* (total ${t.total || 0})`,
      testLines,
      '',
      `*Collections* — Total: ${fmtINR(c.total_collected || 0)}`,
      methodLines,
      `• Opening cash: ${fmtINR(cashCarryOver)}`,
      `• Petty cash today: ${fmtINR(pettyTotal)}`,
      `• Closing / carry-forward cash: ${fmtINR(totalCashInDrawer)}`,
      '',
      '_See email for the full PDF summary._',
    ].join('%0A');
    window.open(`https://wa.me/?text=${lines}`, '_blank', 'noopener,noreferrer');
  };

  if (!isAllowed) return null;

  return (
    <div className="flex-1 overflow-auto bg-gray-100 p-2 sm:p-4" data-testid="eod-report-page">
      {/* Toolbar — not printed. */}
      <div
        className="mb-3 flex items-center justify-between gap-3 bg-white border border-slate-200 rounded shadow-sm px-3 py-2 no-print flex-wrap"
        style={{ width: '100%', maxWidth: `${A4_WIDTH_PX}px`, marginLeft: 'auto', marginRight: 'auto' }}
      >
        <div className="flex items-center gap-2 flex-wrap">
          <CalendarDays size={14} className="text-slate-500" />
          <input
            type="date"
            value={date}
            max={new Date().toISOString().slice(0, 10)}
            onChange={(e) => setDate(e.target.value)}
            data-testid="eod-date-picker"
            className="text-xs px-2 py-1 border border-slate-300 rounded"
          />
          {branches.length > 1 && (
            <>
              <Building2 size={14} className="text-slate-500 ml-2" />
              <select
                value={branchId}
                onChange={(e) => setBranchId(e.target.value)}
                data-testid="eod-branch-select"
                className="text-xs px-2 py-1 border border-slate-300 rounded bg-white"
              >
                <option value="">All branches</option>
                {branches.map((b) => (
                  <option key={b.branch_id} value={b.branch_id}>
                    {b.name}{b.is_primary ? ' · HQ' : ''}
                  </option>
                ))}
              </select>
            </>
          )}
          {loading && <Loader2 size={14} className="text-slate-500 animate-spin" />}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handlePrint}
            data-testid="eod-print-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold bg-slate-800 hover:bg-slate-900 text-white rounded shadow-sm"
          >
            <Printer size={14} /> Print / PDF
          </button>
          <button
            onClick={handleEmail}
            disabled={emailing || !data}
            data-testid="eod-email-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 text-white rounded shadow-sm"
          >
            {emailing ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />} Email
          </button>
          <button
            onClick={handleWhatsApp}
            disabled={!data}
            data-testid="eod-whatsapp-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white rounded shadow-sm"
          >
            <MessageCircle size={14} /> WhatsApp
          </button>
        </div>
      </div>

      {msg && (
        <div
          className="mb-3 text-xs px-3 py-2 rounded border no-print"
          style={{ width: '100%', maxWidth: `${A4_WIDTH_PX}px`, marginLeft: 'auto', marginRight: 'auto' }}
          data-testid="eod-status-msg"
        >
          {msg}
        </div>
      )}

      {/* A4 canvas — the ONLY thing that survives `body.printing-eod-report`. */}
      <div className="overflow-x-auto max-w-full" data-testid="eod-report-canvas-scroll">
      <div
        id="eod-report-canvas"
        data-testid="eod-report-canvas"
        className="bg-white shadow-lg"
        style={{
          width: `${A4_WIDTH_PX}px`,
          minHeight: `${A4_HEIGHT_PX}px`,
          padding: '38px',
          marginLeft: 'auto',
          marginRight: 'auto',
          fontFamily: 'Arial, sans-serif',
          color: '#1f2937',
          boxSizing: 'border-box',
        }}
      >
        <div className="flex flex-col gap-3">
          {/* ---------- Header ---------- */}
          <header className="flex items-start justify-between border-b-2 border-slate-800 pb-2">
            <div>
              <div className="text-lg font-black text-slate-900">{clinic?.name || 'Clinic'}</div>
              <div className="text-[10px] text-slate-500">{clinic?.address_line1 || ''}</div>
            </div>
            <div className="text-right">
              <div className="text-xs uppercase font-bold tracking-widest text-slate-500">End-of-Day Summary</div>
              <div className="text-sm font-bold text-slate-900">{data?.date || date}</div>
            </div>
          </header>

          {/* ---------- Patient Summary ---------- */}
          <SectionCard title="Patient Summary" testid="eod-section-patients">
            <div className="flex gap-2 flex-wrap">
              <StatCard label="New patients"   value={data?.patient_summary?.new_patients ?? '—'}       testid="eod-stat-new" />
              <StatCard label="Returning"      value={data?.patient_summary?.returning_patients ?? '—'} testid="eod-stat-returning" />
              <StatCard label="Total footfall" value={data?.patient_summary?.total_footfall ?? '—'}    testid="eod-stat-footfall" />
            </div>
          </SectionCard>

          {/* ---------- Tests Performed ---------- */}
          <SectionCard title={`Tests Performed · Total ${data?.tests_performed?.total ?? 0}`} testid="eod-section-tests">
            <table className="w-full text-[11px]">
              <thead>
                <tr className="border-b border-slate-200 text-slate-600">
                  <th className="text-left py-1">Test</th>
                  <th className="text-right py-1">Count</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(data?.tests_performed?.by_type || {}).map(([k, v]) => (
                  <tr key={k} className="border-b border-slate-100 last:border-b-0" data-testid={`eod-test-row-${k.toLowerCase().replace(/\s/g, '-')}`}>
                    <td className="py-1 font-semibold text-slate-800">{k}</td>
                    <td className="py-1 text-right font-mono">{v}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </SectionCard>

          {/* ---------- Collections ---------- */}
          <SectionCard title={`Collections · Total ${fmtINR(data?.collections?.total_collected)}`} testid="eod-section-collections">
            <div className="grid grid-cols-2 gap-3">
              {/* By service */}
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">By service</div>
                <table className="w-full text-[11px]">
                  <tbody>
                    {(data?.collections?.by_service || []).slice(0, 8).map((s) => (
                      <tr key={s.name} className="border-b border-slate-100 last:border-b-0">
                        <td className="py-1 truncate max-w-[180px]" title={s.name}>{s.name}</td>
                        <td className="py-1 text-right font-mono">{fmtINR(s.amount)}</td>
                      </tr>
                    ))}
                    {(!data?.collections?.by_service || data.collections.by_service.length === 0) && (
                      <tr><td className="py-1 italic text-slate-400">No revenue logged</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* By payment method + cash drawer */}
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">By payment method</div>
                <table className="w-full text-[11px]">
                  <tbody>
                    {Object.entries(data?.collections?.by_method || {}).map(([k, v]) => (
                      <tr key={k} className="border-b border-slate-100 last:border-b-0">
                        <td className="py-1 capitalize">{k}</td>
                        <td className="py-1 text-right font-mono">{fmtINR(v)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="mt-2 pt-2 border-t border-slate-200 space-y-1">
                  <label className="flex items-center justify-between gap-2 text-[11px] no-print">
                    <span className="font-semibold text-slate-700">Opening / Carry Forward</span>
                    <span className="flex items-center gap-1">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={cashInput}
                        onChange={(e) => onCashInputChange(e.target.value)}
                        disabled={cashInputDisabled}
                        data-testid="eod-cash-carry-input"
                        className="w-24 text-right font-mono border border-slate-300 rounded px-1 py-0.5 disabled:bg-slate-100 disabled:text-slate-400"
                      />
                      <button
                        type="button"
                        onClick={onCashCarrySave}
                        disabled={cashSaving || cashInputDisabled || !cashDirty}
                        data-testid="eod-cash-carry-save-btn"
                        className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-slate-800 hover:bg-slate-900 disabled:bg-slate-300 text-white"
                      >
                        {cashSaving ? '…' : 'Save'}
                      </button>
                    </span>
                  </label>
                  {(cashMeta.opening_by_user_id || cashMsg) && (
                    <div
                      className="text-[10px] text-slate-500 flex items-center justify-between gap-2 no-print"
                      data-testid="eod-cash-carry-meta"
                    >
                      <span>
                        {cashMeta.opening_at
                          ? `Set ${new Date(cashMeta.opening_at).toLocaleString('en-IN')}`
                          : ''}
                      </span>
                      <span className={cashMsg === 'Saved' ? 'text-emerald-600' : 'text-slate-500'}>
                        {cashMsg}
                      </span>
                    </div>
                  )}
                  {!effectiveBranchId && branches.length > 1 && (
                    <div className="text-[10px] italic text-amber-600 no-print" data-testid="eod-cash-carry-branch-hint">
                      Select a branch to enter opening cash for its drawer.
                    </div>
                  )}
                  {/* Print-visible version of the entered value */}
                  <div className="hidden print:flex items-center justify-between text-[11px]">
                    <span className="font-semibold text-slate-700">Opening / Carry Forward</span>
                    <span className="font-mono">{fmtINR(cashCarryOver)}</span>
                  </div>
                  <div className="flex items-center justify-between text-[11px]">
                    <span className="text-slate-700">Today's Cash Collection</span>
                    <span className="font-mono" data-testid="eod-cash-collection">{fmtINR(data?.collections?.by_method?.cash)}</span>
                  </div>
                  <div className="flex items-center justify-between text-[11px]">
                    <span className="text-slate-700">Less: Petty Cash</span>
                    <span className="font-mono text-rose-700" data-testid="eod-petty-cash-total">− {fmtINR(pettyTotal)}</span>
                  </div>
                  <div className="flex items-center justify-between text-[11px] pt-1 border-t border-slate-100">
                    <span className="font-bold text-slate-900">Expected Closing Cash</span>
                    <span className="font-mono font-bold" data-testid="eod-cash-in-drawer">{fmtINR(totalCashInDrawer)}</span>
                  </div>
                  {/* Actual Cash Counted · Cash Variance — Phase 1 (2026-02).
                      Physical observation only. Does NOT modify Opening,
                      Petty, Cash Collection or the Expected Closing formula. */}
                  <label className="flex items-center justify-between gap-2 text-[11px] no-print pt-1">
                    <span className="font-semibold text-slate-700">Actual Cash Counted</span>
                    <span className="flex items-center gap-1">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={actualCashInput}
                        onChange={(e) => onActualCashInputChange(e.target.value)}
                        disabled={cashInputDisabled}
                        placeholder="Not entered"
                        data-testid="eod-actual-cash-input"
                        className="w-24 text-right font-mono border border-slate-300 rounded px-1 py-0.5 disabled:bg-slate-100 disabled:text-slate-400"
                      />
                      <button
                        type="button"
                        onClick={onActualCashSave}
                        disabled={actualCashSaving || cashInputDisabled || !actualCashDirty || actualCashInput === ''}
                        data-testid="eod-actual-cash-save-btn"
                        className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-slate-800 hover:bg-slate-900 disabled:bg-slate-300 text-white"
                      >
                        {actualCashSaving ? '…' : 'Save Cash Count'}
                      </button>
                    </span>
                  </label>
                  {(actualCashMeta.actual_cash_by_user_id || actualCashMsg) && (
                    <div
                      className="text-[10px] text-slate-500 flex items-center justify-between gap-2 no-print"
                      data-testid="eod-actual-cash-meta"
                    >
                      <span>
                        {actualCashMeta.actual_cash_at
                          ? `Counted ${new Date(actualCashMeta.actual_cash_at).toLocaleString('en-IN')}`
                          : ''}
                      </span>
                      <span className={actualCashMsg === 'Saved' ? 'text-emerald-600' : 'text-slate-500'}>
                        {actualCashMsg}
                      </span>
                    </div>
                  )}
                  {/* Print-visible version of the entered value */}
                  <div className="hidden print:flex items-center justify-between text-[11px]">
                    <span className="font-semibold text-slate-700">Actual Cash Counted</span>
                    <span className="font-mono">
                      {actualCashValue == null ? 'Not entered' : fmtINR(actualCashValue)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-[11px]">
                    <span className="text-slate-700">Cash Variance</span>
                    <span
                      className={`font-mono font-bold ${
                        data?.cash_handover?.variance == null
                          ? 'text-slate-400 italic'
                          : data.cash_handover.variance === 0
                            ? 'text-emerald-700'
                            : data.cash_handover.variance < 0
                              ? 'text-rose-700'
                              : 'text-amber-700'
                      }`}
                      data-testid="eod-cash-variance"
                    >
                      {data?.cash_handover?.variance == null
                        ? 'Not calculated'
                        : `${data.cash_handover.variance > 0 ? '+ ' : data.cash_handover.variance < 0 ? '− ' : ''}${fmtINR(Math.abs(data.cash_handover.variance))}`}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-[11px]">
                    <span className="text-slate-700">Status</span>
                    {data?.cash_handover?.variance_status ? (
                      <span
                        data-testid="eod-cash-variance-status"
                        className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
                          data.cash_handover.variance_status === 'MATCHED'
                            ? 'bg-emerald-100 text-emerald-800 border border-emerald-300'
                            : data.cash_handover.variance_status === 'SHORT'
                              ? 'bg-rose-100 text-rose-800 border border-rose-300'
                              : 'bg-amber-100 text-amber-800 border border-amber-300'
                        }`}
                      >
                        {data.cash_handover.variance_status}
                      </span>
                    ) : (
                      <span
                        data-testid="eod-cash-variance-status"
                        className="text-slate-400 italic text-[10px]"
                      >
                        Not entered
                      </span>
                    )}
                  </div>
                  <div className="flex items-center justify-between text-[11px] pt-1 border-t border-slate-100">
                    <span className="text-slate-700">Bank Deposit</span>
                    <span
                      className={`font-mono ${bankTotal > 0 ? 'text-rose-700' : 'text-slate-500'}`}
                      data-testid="eod-bank-deposit-total"
                    >
                      {bankTotal > 0 ? `− ${fmtINR(bankTotal)}` : fmtINR(0)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-[11px]">
                    <span className="font-bold text-slate-900">Cash Remaining After Deposit</span>
                    <span
                      className={`font-mono font-bold ${
                        data?.cash_handover?.cash_remaining == null
                          ? 'text-slate-400 italic'
                          : 'text-slate-900'
                      }`}
                      data-testid="eod-cash-remaining"
                    >
                      {data?.cash_handover?.cash_remaining == null
                        ? 'Not calculated'
                        : fmtINR(data.cash_handover.cash_remaining)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </SectionCard>

          {/* ---------- Petty Cash (2026-02) ---------- */}
          {/* Physical cash movements that leave the drawer for small
              operational spends (auto-rickshaw, chai, stationery, ...).
              Persisted server-side via the SAME cash-outflow endpoint
              introduced in Increment 2 with kind="petty_cash". Total
              flows into the Closing Cash line above. No changes to
              payments / collections semantics. */}
          <SectionCard title={`Petty Cash · Today ${fmtINR(pettyTotal)}`} testid="eod-section-petty-cash">
            <div className="no-print space-y-2">
              <div className="flex flex-wrap items-end gap-2">
                <div className="flex flex-col">
                  <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Amount (₹)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={pettyAmountInput}
                    onChange={(e) => { setPettyAmountInput(e.target.value); setPettyMsg(''); }}
                    disabled={cashInputDisabled}
                    placeholder="0.00"
                    data-testid="eod-petty-amount-input"
                    className="w-28 text-right font-mono text-[11px] border border-slate-300 rounded px-2 py-1 disabled:bg-slate-100"
                  />
                </div>
                <div className="flex flex-col flex-1 min-w-[180px]">
                  <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Reason</label>
                  <input
                    type="text"
                    maxLength={500}
                    value={pettyReasonInput}
                    onChange={(e) => { setPettyReasonInput(e.target.value); setPettyMsg(''); }}
                    disabled={cashInputDisabled}
                    placeholder="e.g. Auto rickshaw to bank"
                    data-testid="eod-petty-reason-input"
                    className="text-[11px] border border-slate-300 rounded px-2 py-1 disabled:bg-slate-100"
                  />
                </div>
                <button
                  type="button"
                  onClick={onPettySave}
                  disabled={pettySaving || cashInputDisabled || !pettyAmountInput || !pettyReasonInput.trim()}
                  data-testid="eod-petty-add-btn"
                  className="text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded bg-emerald-700 hover:bg-emerald-800 disabled:bg-slate-300 text-white"
                >
                  {pettySaving ? 'Saving…' : 'Add'}
                </button>
              </div>
              {pettyMsg && (
                <div
                  className={`text-[10px] ${pettyMsg === 'Added' ? 'text-emerald-600' : 'text-rose-600'}`}
                  data-testid="eod-petty-msg"
                >
                  {pettyMsg}
                </div>
              )}
              {!effectiveBranchId && branches.length > 1 && (
                <div className="text-[10px] italic text-amber-600" data-testid="eod-petty-branch-hint">
                  Select a branch to record petty cash for its drawer.
                </div>
              )}
            </div>

            {pettyEntries.length ? (
              <table className="w-full text-[11px] mt-2" data-testid="eod-petty-list">
                <thead>
                  <tr className="border-b border-slate-200 text-slate-600">
                    <th className="text-right py-1 pr-2 w-24">Amount</th>
                    <th className="text-left py-1">Reason</th>
                    <th className="text-left py-1 w-32 hidden sm:table-cell">Time</th>
                    <th className="text-left py-1 w-40 hidden sm:table-cell">Entered by</th>
                  </tr>
                </thead>
                <tbody>
                  {pettyEntries.map((o) => (
                    <tr
                      key={o.outflow_id}
                      className="border-b border-slate-100 last:border-b-0"
                      data-testid={`eod-petty-row-${o.outflow_id}`}
                    >
                      <td className="py-1 pr-2 text-right font-mono text-rose-700">− {fmtINR(o.amount)}</td>
                      <td className="py-1 truncate max-w-[280px]" title={o.reason}>{o.reason}</td>
                      <td className="py-1 text-slate-500 hidden sm:table-cell">
                        {o.created_at ? new Date(o.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '—'}
                      </td>
                      <td className="py-1 text-slate-500 hidden sm:table-cell">
                        <span className="text-[10px] font-mono">{o.actor_user_id || '—'}</span>
                        {o.actor_role && <span className="text-[10px] text-slate-400 ml-1">({o.actor_role})</span>}
                      </td>
                    </tr>
                  ))}
                  <tr className="border-t-2 border-slate-300">
                    <td className="py-1 pr-2 text-right font-mono font-bold text-slate-900" data-testid="eod-petty-list-total">− {fmtINR(pettyTotal)}</td>
                    <td className="py-1 font-bold text-slate-900" colSpan={3}>Total Petty Cash Today</td>
                  </tr>
                </tbody>
              </table>
            ) : (
              <div className="text-[11px] italic text-slate-400 mt-2" data-testid="eod-petty-empty">
                No petty cash entries yet.
              </div>
            )}
          </SectionCard>

          {/* ---------- Bank Deposit (2026-02 · Phase 2A) ---------- */}
          {/* Records physical cash moved out of the drawer to the
              bank. Uses the SAME `cash_outflows` endpoint from
              Increment 2 with kind="bank_deposit". Server role gate:
              `_BANK_DEPOSIT_ROLES` = clinic_owner / super_admin /
              founder / accounts / front_desk. Cross-branch and cross-
              tenant isolation are enforced server-side by
              `_assert_cash_position_branch`. `cash_remaining` on the
              drawer block reflects `actual_cash − bank_deposit_total`
              and is only computed when a physical count exists. */}
          <SectionCard title={`Bank Deposit · Today ${fmtINR(bankTotal)}`} testid="eod-section-bank-deposit">
            {canRecordBankDeposit && (
              <div className="no-print space-y-2">
                <div className="flex flex-wrap items-end gap-2">
                  <div className="flex flex-col">
                    <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Amount (₹)</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={bankAmountInput}
                      onChange={(e) => { setBankAmountInput(e.target.value); setBankMsg(''); }}
                      disabled={cashInputDisabled}
                      placeholder="0.00"
                      data-testid="eod-bank-amount-input"
                      className="w-28 text-right font-mono text-[11px] border border-slate-300 rounded px-2 py-1 disabled:bg-slate-100"
                    />
                  </div>
                  <div className="flex flex-col flex-1 min-w-[220px]">
                    <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Reference / Note</label>
                    <input
                      type="text"
                      maxLength={200}
                      value={bankReferenceInput}
                      onChange={(e) => { setBankReferenceInput(e.target.value); setBankMsg(''); }}
                      disabled={cashInputDisabled}
                      placeholder="e.g. HDFC deposit slip 12345"
                      data-testid="eod-bank-reference-input"
                      className="text-[11px] border border-slate-300 rounded px-2 py-1 disabled:bg-slate-100"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={onBankDepositSave}
                    disabled={bankSaving || cashInputDisabled || !bankAmountInput || !bankReferenceInput.trim()}
                    data-testid="eod-bank-add-btn"
                    className="text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded bg-indigo-700 hover:bg-indigo-800 disabled:bg-slate-300 text-white"
                  >
                    {bankSaving ? 'Saving…' : 'Record Deposit'}
                  </button>
                </div>
                {bankMsg && (
                  <div
                    className={`text-[10px] ${bankMsg === 'Recorded' ? 'text-emerald-600' : 'text-rose-600'}`}
                    data-testid="eod-bank-msg"
                  >
                    {bankMsg}
                  </div>
                )}
                {!effectiveBranchId && branches.length > 1 && (
                  <div className="text-[10px] italic text-amber-600" data-testid="eod-bank-branch-hint">
                    Select a branch to record a bank deposit for its drawer.
                  </div>
                )}
              </div>
            )}
            {!canRecordBankDeposit && (
              <div className="text-[10px] italic text-slate-500 no-print" data-testid="eod-bank-role-hint">
                Your role can view but not record bank deposits.
              </div>
            )}

            {bankEntries.length ? (
              <table className="w-full text-[11px] mt-2" data-testid="eod-bank-list">
                <thead>
                  <tr className="border-b border-slate-200 text-slate-600">
                    <th className="text-right py-1 pr-2 w-24">Amount</th>
                    <th className="text-left py-1">Reference</th>
                    <th className="text-left py-1 w-32 hidden sm:table-cell">Time</th>
                    <th className="text-left py-1 w-40 hidden sm:table-cell">Recorded by</th>
                  </tr>
                </thead>
                <tbody>
                  {bankEntries.map((o) => (
                    <tr
                      key={o.outflow_id}
                      className="border-b border-slate-100 last:border-b-0"
                      data-testid={`eod-bank-row-${o.outflow_id}`}
                    >
                      <td className="py-1 pr-2 text-right font-mono text-rose-700">− {fmtINR(o.amount)}</td>
                      <td className="py-1 truncate max-w-[280px]" title={o.reference || o.reason}>
                        {o.reference || o.reason}
                      </td>
                      <td className="py-1 text-slate-500 hidden sm:table-cell">
                        {o.created_at ? new Date(o.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                      </td>
                      <td className="py-1 text-slate-500 hidden sm:table-cell">
                        <span className="text-[10px] font-mono">{o.actor_user_id || '—'}</span>
                        {o.actor_role && <span className="text-[10px] text-slate-400 ml-1">({o.actor_role})</span>}
                      </td>
                    </tr>
                  ))}
                  <tr className="border-t-2 border-slate-300">
                    <td className="py-1 pr-2 text-right font-mono font-bold text-slate-900" data-testid="eod-bank-list-total">− {fmtINR(bankTotal)}</td>
                    <td className="py-1 font-bold text-slate-900" colSpan={3}>Total Bank Deposits Today</td>
                  </tr>
                </tbody>
              </table>
            ) : (
              <div className="text-[11px] italic text-slate-400 mt-2" data-testid="eod-bank-empty">
                No bank deposits yet.
              </div>
            )}
          </SectionCard>

          {/* ---------- Referrals ---------- */}
          <SectionCard title="Referrals" testid="eod-section-referrals">
            {(data?.referrers || []).length ? (
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="border-b border-slate-200 text-slate-600">
                    <th className="text-left py-1">Referring Doctor</th>
                    <th className="text-right py-1">Patients</th>
                  </tr>
                </thead>
                <tbody>
                  {data.referrers.map((r) => (
                    <tr key={r.doctor_id} className="border-b border-slate-100 last:border-b-0">
                      <td className="py-1 font-semibold text-slate-800">{r.name}</td>
                      <td className="py-1 text-right font-mono">{r.patient_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <div className="text-[11px] italic text-slate-400">No referrals logged today</div>}
          </SectionCard>

          {/* ---------- Products / Accessories Sold ---------- */}
          <SectionCard title="Products / Accessories Sold" testid="eod-section-products">
            {(data?.products_sold || []).length ? (
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="border-b border-slate-200 text-slate-600">
                    <th className="text-left py-1">Item</th>
                    <th className="text-right py-1">Qty</th>
                    <th className="text-right py-1">Unit</th>
                    <th className="text-right py-1">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {data.products_sold.slice(0, 12).map((r) => (
                    <tr key={r.name} className="border-b border-slate-100 last:border-b-0">
                      <td className="py-1 truncate max-w-[280px]" title={r.name}>{r.name}</td>
                      <td className="py-1 text-right font-mono">{r.quantity}</td>
                      <td className="py-1 text-right font-mono">{fmtINR(r.unit_price)}</td>
                      <td className="py-1 text-right font-mono">{fmtINR(r.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <div className="text-[11px] italic text-slate-400">No sales logged today</div>}
          </SectionCard>

          {/* ---------- Stock Remaining ---------- */}
          <SectionCard title={`Stock Remaining · ${data?.stock_remaining?.hearing_aid_units ?? 0} HA units in stock`} testid="eod-section-stock">
            {(data?.stock_remaining?.accessories || []).length ? (
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="border-b border-slate-200 text-slate-600">
                    <th className="text-left py-1">Item</th>
                    <th className="text-right py-1">Qty on hand</th>
                  </tr>
                </thead>
                <tbody>
                  {data.stock_remaining.accessories.slice(0, 15).map((r) => (
                    <tr key={r.sku || r.product_name} className="border-b border-slate-100 last:border-b-0">
                      <td className="py-1">
                        <div className="truncate max-w-[420px]">{r.product_name || `${r.brand || ''} ${r.model || ''}`.trim()}</div>
                      </td>
                      <td className="py-1 text-right font-mono">{r.qty_on_hand}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : <div className="text-[11px] italic text-slate-400">No accessories in stock</div>}
          </SectionCard>

          {/* ---------- Signature strip ---------- */}
          <div className="mt-2 pt-2 border-t border-slate-200 flex items-end justify-between text-[10px] text-slate-500">
            <div>Generated by <strong>{user?.name || user?.email}</strong> · {new Date().toLocaleString('en-IN')}</div>
            <div>AUDINEXA · EOD Summary v1</div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}
